<template>
	<div class="pictureviewer" ref="rootEl" v-if="imageList?.length > 0" @click="onClick()">
		<div class="holder" ref="holder">
			<img :src="pictureURL" class="picture" @click.stop="" ref="image" @load="onImageLoaded()">

			<div class="navigation" ref="nav">
				<button class="prev" @click="prev($event)">
					<img src="@/assets/icons/arrow_prev.svg" alt="prev">
				</button>
				<button class="next" @click="next($event)">
					<img src="@/assets/icons/arrow_next.svg" alt="next">
				</button>
			</div>
		</div>

			<button class="closeBt" @click="close()">
				<img src="@/assets/icons/close.svg" alt="next">
			</button>
	</div>
</template>

<script setup lang="ts">
import { componentsStore as useComponentsStore } from '@/stores/components';
import gsap from 'gsap/all';
import { computed, nextTick, ref, useTemplateRef, watch } from 'vue';

const componentsStore = useComponentsStore();

const rootEl = useTemplateRef<HTMLDivElement>('rootEl');
const holder = useTemplateRef<HTMLDivElement>('holder');
const image = useTemplateRef<HTMLImageElement>('image');
const nav = useTemplateRef<HTMLDivElement>('nav');

const imageIndex = ref<number>(0);

let onLoadAnimation:(()=>void) | null = null;

const imageList = computed<string[]>(() => componentsStore.gallery);

const pictureURL = computed<string>(() => imageList.value[ imageIndex.value ]);

watch(()=>componentsStore.gallery, async ()=>{
	imageIndex.value = 0;
	if(imageList.value.length > 0) {
		await nextTick();
		open();
	}
});

function open():void {
	gsap.fromTo(rootEl.value!, { opacity:0 }, { opacity:1, duration:1, clearProps:"all", ease:"sine.out" });
	gsap.fromTo(holder.value!, { y:50 }, { y:0, duration:.5, clearProps:"all", ease:"sine.out" });
	gsap.from(nav.value!, { opacity:0, width:"80vw", duration:.5, delay:.5, clearProps:"all", ease:"sine.out" });
}

function close():void {
	gsap.to(rootEl.value!, { opacity:0, duration:.5, ease:"sine.inOut", onComplete:()=> {
		componentsStore.closeGallery();
	} });
}

function prev(e:MouseEvent):void {
	e.stopPropagation();
	const img = image.value!;
	gsap.to(img, { x:25, opacity:0, duration:.25, ease:"sine.in", onComplete:()=> {
		imageIndex.value --;
		if(imageIndex.value == -1) imageIndex.value = imageList.value.length - 1;

		onLoadAnimation = ()=> {
			gsap.fromTo(img, {x:-25}, { x:0, opacity:1, duration:.25, clearProps:"all", ease:"sine.out" });
		}
	} });
}

function next(e:MouseEvent):void {
	e.stopPropagation();
	const img = image.value!;
	gsap.to(img, { x:-25, opacity:0, duration:.25, ease:"sine.in", onComplete:()=> {
		imageIndex.value ++;
		imageIndex.value = imageIndex.value % imageList.value.length;
		if(imageIndex.value == -1) imageIndex.value = imageList.value.length - 1;
		onLoadAnimation = ()=> {
			gsap.fromTo(img, {x:25}, { x:0, opacity:1, duration:.25, clearProps:"all", ease:"sine.out" });
		}
	} });
}

function onImageLoaded():void {
	if(onLoadAnimation) {
		onLoadAnimation();
		onLoadAnimation = null;
	}
}

function onClick():void {
	close()
}
</script>

<style scoped lang="less">
.pictureviewer{
	background: rgba(0, 0, 0, .8);
	backdrop-filter: blur(10px);
	width: 100vw;
	height: 100vh;
	position: fixed;
	top:0;
	left:0;
	z-index: 10;

	.closeBt {
		position: fixed;
		top: 1em;
		right: 1em;
		width: 2em;
		height: 2em;
		background: none;
		transform-origin: center center;
		transition: transform .3s;
		padding: 0;
		margin: 0;

		&:hover {
			transform: rotate(90deg)
		}
	}

	.holder {
		.center();
		position: absolute;
		.picture {
			max-height: calc(100vh - 10em);
			max-width: calc(100vw - 2em);
			object-fit: contain;
		}

		.navigation {
			.center();
			top: 50%;
			// overflow: hidden;
			position: absolute;
			width: 100vw;
			display: flex;
			flex-direction: row;
			justify-content: space-between;
			pointer-events: none;

			button {
				background: none;
				transition: all 1s;
				padding: 2em;
				pointer-events: all;
				img {
					width: 2em;
				}
				&.prev {
					&:hover {
						margin-left: -1em;
						padding-right: 3em;
					}
				}
				&.next {
					&:hover {
						margin-right: -1em;
						padding-left: 3em;
					}
				}
			}
		}
	}
}
@media only screen and (max-width: 480px) {
.pictureviewer{
	.holder {
		.navigation {
			position: unset;
			transform: unset;
			top: unset;
			left: unset;
			width: 40%;
			margin: auto;

			&.noPrev {
				justify-content: space-between;
			}
			
			button {
				padding: .5em;
				img {
					width: 1em;
				}

				&.prev:hover,
				&.next:hover {
					margin-right: unset;
					margin-left: unset;
				}
			}
		}
	}
}
}
</style>